import {vnTemplate} from '../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate';

declare var ko: any;

export class humanBotScoreDashboard_template extends vnTemplate {

    protected getTemplateName(): string {

        return 'humanBotScoreDashboard_template';
    }

    protected getMainElementClasses(): Array<string> {

        return ['human-bot-score-dashboard'];
    }

    protected getTemplateHtml(): string {

        return `
            <div class="page-content">
                <div id="human-bot-score-results">
                    <div class="hb-loader-overlay" data-bind="visible: isLoading">
                        <div class="hb-loader">
                            <span></span>
                            <strong>Loading report data...</strong>
                        </div>
                    </div>
                    <div class="page-header">
                        <h2 class="title-page">Human/Bot Score</h2>
                    </div>

                    <section class="hb-section hb-intro">
                        <div class="hb-intro-copy">
                            <h3>What this page does</h3>
                            <p>
                                This dashboard compares the old raw suspicious flag with the new scoring system.
                                The new system scores raw-log hits only, keeps the latest rolling hits per IP, then compares human hits with suspicious hits.
                                A valid page view adds only a small baseline score; it is not enough to call the IP human.
                                The final IP decision is now intentionally simple: any hard-bot hit makes the IP bot; otherwise more than half suspicious hits makes it suspicious; otherwise more than half human hits makes it human. If there is no human majority, it stays suspicious.
                                Logged-in, active-session, user-reference, and ishc hits remain visible as evidence, but they do not override the final rolling decision.
                                Real event signals are intentionally disabled for now and should be enabled once all brands have event coverage.
                            </p>
                        </div>
                        <div class="hb-run-summary">
                            <span>Account</span>
                            <strong data-bind="text: account"></strong>
                            <span>Last loaded</span>
                            <strong data-bind="text: lastRunText"></strong>
                        </div>
                    </section>

                    <section class="hb-section hb-filters">
                        <div class="hb-filter-grid">
                            <label>
                                <span>Brand</span>
                                <select data-bind="options: brands, optionsText: 'label', optionsValue: 'value', value: selectedBrand"></select>
                            </label>
                            <label>
                                <span>Date</span>
                                <input type="date" data-bind="value: reportDate" />
                            </label>
                            <label>
                                <span>Lookback days</span>
                                <input type="number" min="1" max="365" data-bind="value: lookbackDays" />
                            </label>
                            <label>
                                <span>Rolling hits</span>
                                <input type="number" min="1" max="100" data-bind="value: rollingHitLimit" />
                            </label>
                            <label>
                                <span>Config version</span>
                                <input type="text" data-bind="value: configVersion" />
                            </label>
                            <label>
                                <span>Rows</span>
                                <input type="number" min="1" max="1000" data-bind="value: limit" />
                            </label>
                            <button class="button-default icon i-refresh" type="button" data-bind="click: onRefresh, disable: isLoading">Refresh</button>
                        </div>
                    </section>

                    <section class="hb-section hb-explain">
                        <div class="hb-steps">
                            <h3>Simple steps</h3>
                            <ol>
                                <li><strong>Build the new score.</strong> The PHP job reads raw logs and writes only the new scoring tables.</li>
                                <li><strong>Score each eligible hit.</strong> Page views get a tiny baseline only. Engagement and login/session signals matter much more.</li>
                                <li><strong>Roll up by IP.</strong> The report uses the latest hits inside the lookback window, usually the last 10 hits in 30 days.</li>
                                <li><strong>Choose the final class.</strong> Any hard-bot hit means bot. If bot does not win, more than half suspicious hits means suspicious. If suspicious does not win, more than half human hits means human. Otherwise it stays suspicious.</li>
                                <li><strong>Compare with old suspicious.</strong> The old raw isSuspicious flag is shown only as a baseline number.</li>
                            </ol>
                        </div>
                        <div class="hb-flow" aria-label="Human bot scoring flow">
                            <div class="hb-flow-step">
                                <span>1</span>
                                <strong>Raw logs</strong>
                                <small>Selected brand and date</small>
                            </div>
                            <div class="hb-flow-arrow"></div>
                            <div class="hb-flow-step">
                                <span>2</span>
                                <strong>Eligible hits</strong>
                                <small>Page views get a tiny baseline, not a human confirmation</small>
                            </div>
                            <div class="hb-flow-arrow"></div>
                            <div class="hb-flow-step">
                                <span>3</span>
                                <strong>Rolling IP</strong>
                                <small>Last hits in the lookback window</small>
                            </div>
                            <div class="hb-flow-arrow"></div>
                            <div class="hb-flow-step">
                                <span>4</span>
                                <strong>Decision</strong>
                                <small>Any bot, then suspicious majority, then human majority</small>
                            </div>
                            <div class="hb-flow-arrow"></div>
                            <div class="hb-flow-step">
                                <span>5</span>
                                <strong>Compare</strong>
                                <small>New result versus old suspicious flag</small>
                            </div>
                        </div>
                    </section>

                    <section class="hb-section hb-calc">
                        <div class="hb-section-heading">
                            <h3>Calculation Diagram</h3>
                            <p>Read this left to right. The old isSuspicious flag is not part of this calculation.</p>
                        </div>
                        <div class="hb-calc-diagram">
                            <div class="hb-calc-node">
                                <span>1</span>
                                <strong>Raw page view</strong>
                                <small>Eligible for scoring. Adds only a small baseline because bots can request pages too.</small>
                            </div>
                            <div class="hb-calc-node">
                                <span>2</span>
                                <strong>Human score</strong>
                                <small>Starts at 50. Engagement and login/session add points. No engagement, robot, bad user-agent, trap page, or bad permalink remove points.</small>
                            </div>
                            <div class="hb-calc-node danger">
                                <span>3</span>
                                <strong>Bot detection</strong>
                                <small>No weighted bot score. Trap page, robot classification, or known bad user-agent makes the hit bot.</small>
                            </div>
                            <div class="hb-calc-node">
                                <span>4</span>
                                <strong>Last hits per IP</strong>
                                <small>Use the latest rolling hits for that IP in the lookback window.</small>
                            </div>
                            <div class="hb-calc-node decision">
                                <span>5</span>
                                <strong>Final IP class</strong>
                                <small>One hard-bot hit makes the IP bot. Suspicious and human require more than half of rolling hits. Confirmed-human evidence no longer overrides the final class.</small>
                            </div>
                        </div>
                    </section>

                    <div class="hb-status" data-bind="visible: isLoading">Loading report data...</div>
                    <div class="hb-status hb-error" data-bind="visible: errorMessage, text: errorMessage"></div>

                    <section class="hb-section" data-bind="visible: hasData">
                        <div class="hb-section-heading">
                            <h3>Headline comparison</h3>
                            <p>Old suspicious is only a baseline. It is not used by the new score.</p>
                        </div>
                        <div class="hb-score-grid" data-bind="foreach: scoreCards">
                            <div class="hb-score-tile" data-bind="css: tone">
                                <span data-bind="text: label"></span>
                                <strong data-bind="text: value"></strong>
                                <small data-bind="text: detail"></small>
                            </div>
                        </div>
                        <div class="hb-comparison-chart" data-bind="foreach: comparisonBars">
                            <div class="hb-chart-row">
                                <span data-bind="text: label"></span>
                                <div class="hb-chart-track">
                                    <div class="hb-chart-bar" data-bind="style: {width: width}, css: tone"></div>
                                </div>
                                <strong data-bind="text: value"></strong>
                            </div>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasTrend">
                        <div class="hb-section-heading">
                            <h3>Daily traffic spike detection</h3>
                            <p data-bind="text: spikeRuleText"></p>
                        </div>
                        <div class="hb-trend-card">
                            <div class="hb-trend-legend" data-bind="foreach: trendLegend">
                                <span data-bind="css: tone"><i></i><strong data-bind="text: label"></strong><em data-bind="text: value"></em></span>
                            </div>
                            <div class="hb-flatness-summary" data-bind="text: flatnessSummary"></div>
                            <div class="hb-flatness-grid" data-bind="foreach: spikeSummaryCards">
                                <div class="hb-flatness-card" data-bind="css: tone">
                                    <span data-bind="text: label"></span>
                                    <strong data-bind="text: value"></strong>
                                    <small data-bind="text: detail"></small>
                                </div>
                            </div>
                            <svg class="hb-trend-svg" viewBox="0 0 640 240" preserveAspectRatio="none" role="img" aria-label="Traffic trend chart">
                                <line x1="32" y1="28" x2="32" y2="208"></line>
                                <line x1="32" y1="208" x2="620" y2="208"></line>
                                <polyline class="total" data-bind="attr: {points: totalTrafficTrendPoints}"></polyline>
                                <polyline class="old-exclude" data-bind="attr: {points: oldExcludedTrafficTrendPoints}"></polyline>
                                <polyline class="new-exclude" data-bind="attr: {points: newExcludedTrafficTrendPoints}"></polyline>
                                <polyline class="bot" data-bind="attr: {points: botTrafficTrendPoints}"></polyline>
                                <g data-bind="foreach: totalTrafficSpikeMarkers">
                                    <circle class="hb-spike-marker total" r="5" data-bind="attr: {cx: x, cy: y}"><title data-bind="text: title"></title></circle>
                                </g>
                                <g data-bind="foreach: oldExcludedTrafficSpikeMarkers">
                                    <circle class="hb-spike-marker old-exclude" r="5" data-bind="attr: {cx: x, cy: y}"><title data-bind="text: title"></title></circle>
                                </g>
                                <g data-bind="foreach: newExcludedTrafficSpikeMarkers">
                                    <circle class="hb-spike-marker new-exclude" r="5" data-bind="attr: {cx: x, cy: y}"><title data-bind="text: title"></title></circle>
                                </g>
                                <g data-bind="foreach: botTrafficSpikeMarkers">
                                    <circle class="hb-spike-marker bot" r="5" data-bind="attr: {cx: x, cy: y}"><title data-bind="text: title"></title></circle>
                                </g>
                            </svg>
                            <div class="hb-trend-axis">
                                <span data-bind="text: trendStartLabel"></span>
                                <strong data-bind="text: trendMaxLabel"></strong>
                                <span data-bind="text: trendEndLabel"></span>
                            </div>
                            <div class="hb-spike-table-wrap" data-bind="visible: spikeTableRows().length > 0">
                                <h4>Spike days</h4>
                                <table class="list-results hb-table hb-spike-table">
                                    <thead>
                                        <tr>
                                            <th><span>Line</span></th>
                                            <th><span>Date</span></th>
                                            <th><span>Value</span></th>
                                            <th><span>Above baseline</span></th>
                                        </tr>
                                    </thead>
                                    <tbody data-bind="foreach: spikeTableRows">
                                        <tr>
                                            <td data-bind="text: label"></td>
                                            <td data-bind="text: date"></td>
                                            <td data-bind="text: value"></td>
                                            <td data-bind="text: increase"></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasSpikeAttribution">
                        <div class="hb-section-heading">
                            <h3>Spike attribution</h3>
                            <p data-bind="text: spikeAttributionDescription"></p>
                        </div>
                        <div class="hb-attribution-summary" data-bind="foreach: spikeAttributionRollups">
                            <div class="hb-attribution-card" data-bind="css: tone">
                                <span data-bind="text: report_date"></span>
                                <strong data-bind="text: bucket_label"></strong>
                                <small><b data-bind="text: request_count_text"></b><em data-bind="text: request_pct_text"></em></small>
                                <small data-bind="text: detail"></small>
                            </div>
                        </div>
                        <div class="hb-filter-grid hb-filter-grid-secondary">
                            <label>
                                <span>Top IP bucket</span>
                                <select data-bind="options: spikeAttributionBucketOptions, optionsText: 'label', optionsValue: 'value', value: selectedSpikeAttributionBucket"></select>
                            </label>
                            <div class="hb-attribution-focus">
                                <span>Focus date</span>
                                <strong data-bind="text: spikeAttributionFocusDate"></strong>
                            </div>
                        </div>
                        <div class="gradient-overflow">
                            <table class="list-results hb-table hb-attribution-table">
                                <thead>
                                    <tr>
                                        <th><span>Date</span></th>
                                        <th><span>IP</span></th>
                                        <th><span>Brand</span></th>
                                        <th><span>Bucket</span></th>
                                        <th><span>Class</span></th>
                                        <th><span>Requests</span></th>
                                        <th><span>Hits</span></th>
                                        <th><span>Human</span></th>
                                        <th><span>Suspicious</span></th>
                                        <th><span>Bot</span></th>
                                        <th><span>Avg human</span></th>
                                        <th><span>Confirmed</span></th>
                                        <th><span>Reasons</span></th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: spikeAttributionTopIps">
                                    <tr data-bind="visible: $parents[0].selectedSpikeAttributionBucket() === '' || bucket === $parents[0].selectedSpikeAttributionBucket()">
                                        <td data-bind="text: report_date"></td>
                                        <td data-bind="text: ip"></td>
                                        <td data-bind="text: brand"></td>
                                        <td data-bind="text: bucket_label"></td>
                                        <td><span class="hb-pill" data-bind="text: final_ip_classification, css: final_ip_classification"></span></td>
                                        <td data-bind="text: request_count_text"></td>
                                        <td data-bind="text: rolling_hit_count"></td>
                                        <td data-bind="text: human_hits"></td>
                                        <td data-bind="text: suspicious_hits"></td>
                                        <td data-bind="text: bot_hits"></td>
                                        <td data-bind="text: avg_human_score"></td>
                                        <td data-bind="text: has_confirmed_human == 1 ? 'yes' : 'no'"></td>
                                        <td data-bind="text: final_reasons_text"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasData">
                        <div class="hb-section-heading">
                            <h3>Formula and rules</h3>
                            <p>The text below is generated from the active scoring config.</p>
                        </div>
                        <div class="hb-formula-grid">
                            <div class="hb-formula-block">
                                <h4>Human hit score</h4>
                                <p data-bind="text: baseFormulaSummary"></p>
                                <ul data-bind="foreach: basePositiveSignals">
                                    <li><strong data-bind="text: label"></strong><span data-bind="text: detail"></span></li>
                                </ul>
                            </div>
                            <div class="hb-formula-block">
                                <h4>Bot detection</h4>
                                <p data-bind="text: botFormulaSummary"></p>
                                <ul data-bind="foreach: botThresholds">
                                    <li><strong data-bind="text: label"></strong><span data-bind="text: detail"></span></li>
                                </ul>
                                <ul data-bind="foreach: botSignals">
                                    <li><strong data-bind="text: label"></strong><span data-bind="text: detail"></span></li>
                                </ul>
                            </div>
                            <div class="hb-formula-block">
                                <h4>Final IP rules</h4>
                                <p>Final class is calculated from the saved rolling IP summary table. Each rule below shows the active value and what it means.</p>
                                <ul data-bind="foreach: finalRules">
                                    <li><strong data-bind="text: label"></strong><span data-bind="text: detail"></span></li>
                                </ul>
                            </div>
                        </div>
                        <div class="hb-excluded">
                            <h4>Excluded from hit scoring</h4>
                            <div data-bind="foreach: excludedHitFields">
                                <span data-bind="text: $data"></span>
                            </div>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasData">
                        <div class="hb-section-heading">
                            <h3>Coverage</h3>
                            <p>Coverage shows what signals were available for the selected brand and date.</p>
                        </div>
                        <div class="gradient-overflow">
                            <table class="list-results hb-table">
                                <thead>
                                    <tr>
                                        <th><span>Metric</span></th>
                                        <th><span>Description</span></th>
                                        <th><span>IPs</span></th>
                                        <th><span>IP %</span></th>
                                        <th><span>Requests</span></th>
                                        <th><span>Request %</span></th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: coverage">
                                    <tr>
                                        <td data-bind="text: metric"></td>
                                        <td data-bind="text: description"></td>
                                        <td data-bind="text: $parents[0].formatNumber(ip_count)"></td>
                                        <td data-bind="text: $parents[0].formatPercent(ip_pct)"></td>
                                        <td data-bind="text: $parents[0].formatNumber(request_count)"></td>
                                        <td data-bind="text: $parents[0].formatPercent(request_pct)"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasData">
                        <div class="hb-section-heading">
                            <h3>Buckets</h3>
                            <p>Buckets show where the new model agrees or disagrees with the old suspicious flag.</p>
                        </div>
                        <div class="gradient-overflow">
                            <table class="list-results hb-table">
                                <thead>
                                    <tr>
                                        <th><span>Bucket</span></th>
                                        <th><span>IPs</span></th>
                                        <th><span>IP %</span></th>
                                        <th><span>Requests</span></th>
                                        <th><span>Request %</span></th>
                                        <th><span>Avg human</span></th>
                                        <th><span>Hard bot hit %</span></th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: buckets">
                                    <tr>
                                        <td data-bind="text: bucket"></td>
                                        <td data-bind="text: $parents[0].formatNumber(ip_count)"></td>
                                        <td data-bind="text: $parents[0].formatPercent(ip_pct)"></td>
                                        <td data-bind="text: $parents[0].formatNumber(request_count)"></td>
                                        <td data-bind="text: $parents[0].formatPercent(request_pct)"></td>
                                        <td data-bind="text: $parents[0].formatDecimal(avg_human_score)"></td>
                                        <td data-bind="text: $parents[0].formatPercent(hard_bot_hit_pct)"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: hasData">
                        <div class="hb-section-heading">
                            <h3>IP drilldown list</h3>
                            <p>Use these filters to review the IPs that need attention.</p>
                        </div>

                        <div class="hb-filter-grid hb-filter-grid-secondary">
                            <label>
                                <span>Final class</span>
                                <select data-bind="value: finalClassificationFilter">
                                    <option value="">Any</option>
                                    <option value="human">Human</option>
                                    <option value="suspicious">Suspicious</option>
                                    <option value="bot">Bot</option>
                                </select>
                            </label>
                            <label>
                                <span>Old suspicious</span>
                                <select data-bind="value: oldSuspiciousFilter">
                                    <option value="">Any</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                            </label>
                            <label>
                                <span>Bucket</span>
                                <select data-bind="options: bucketOptions, optionsText: 'label', optionsValue: 'value', value: bucketFilter"></select>
                            </label>
                            <label>
                                <span>Min human</span>
                                <input type="number" min="0" max="100" data-bind="value: minHumanScore" />
                            </label>
                            <label>
                                <span>Max human</span>
                                <input type="number" min="0" max="100" data-bind="value: maxHumanScore" />
                            </label>
                            <label>
                                <span>Hard bot</span>
                                <select data-bind="value: hardBotFilter">
                                    <option value="">Any</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                            </label>
                            <label>
                                <span>Confirmed human</span>
                                <select data-bind="value: confirmedHumanFilter">
                                    <option value="">Any</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                            </label>
                            <button class="button-default icon i-search2" type="button" data-bind="click: onRefresh, disable: isLoading">Apply</button>
                        </div>

                        <div class="gradient-overflow">
                            <table class="list-results hb-table hb-candidate-table">
                                <thead>
                                    <tr>
                                        <th><span>IP</span></th>
                                        <th><span>Old</span></th>
                                        <th><span>New class</span></th>
                                        <th><span>Hits</span></th>
                                        <th><span>Human hits</span></th>
                                        <th><span>Suspicious hits</span></th>
                                        <th><span>Bot hits</span></th>
                                        <th><span>Avg human</span></th>
                                        <th><span>Hard bot</span></th>
                                        <th><span>Confirmed</span></th>
                                        <th><span>Requests</span></th>
                                        <th><span>Reasons</span></th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: candidates">
                                    <tr>
                                        <td><button type="button" class="hb-link" data-bind="text: ip, click: $parents[0].onCandidateClick"></button></td>
                                        <td data-bind="text: old_is_suspicious == 1 ? 'yes' : 'no'"></td>
                                        <td><span class="hb-pill" data-bind="text: final_ip_classification, css: final_ip_classification"></span></td>
                                        <td data-bind="text: $parents[0].formatNumber(rolling_hit_count)"></td>
                                        <td data-bind="text: $parents[0].formatNumber(human_hits)"></td>
                                        <td data-bind="text: $parents[0].formatNumber(suspicious_hits)"></td>
                                        <td data-bind="text: $parents[0].formatNumber(bot_hits)"></td>
                                        <td data-bind="text: $parents[0].formatDecimal(avg_human_score)"></td>
                                        <td data-bind="text: has_hard_bot == 1 ? 'yes' : 'no'"></td>
                                        <td data-bind="text: has_confirmed_human == 1 ? 'yes' : 'no'"></td>
                                        <td data-bind="text: $parents[0].formatNumber(request_count)"></td>
                                        <td data-bind="text: $parents[0].formatReasons(final_reasons)"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="hb-section" data-bind="visible: selectedIp">
                        <div class="hb-section-heading hb-detail-heading">
                            <div>
                                <h3>IP detail</h3>
                                <p data-bind="text: selectedIpSummary"></p>
                            </div>
                            <button class="button-default icon i-close" type="button" data-bind="click: onClearIpDetail">Close</button>
                        </div>
                        <div class="gradient-overflow">
                            <table class="list-results hb-table">
                                <thead>
                                    <tr>
                                        <th><span>Rank</span></th>
                                        <th><span>Time</span></th>
                                        <th><span>Permalink</span></th>
                                        <th><span>Human score</span></th>
                                        <th><span>Hard bot</span></th>
                                        <th><span>Human class</span></th>
                                        <th><span>Bot class</span></th>
                                        <th><span>Flags</span></th>
                                        <th><span>Reasons</span></th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: ipDetail">
                                    <tr>
                                        <td data-bind="text: hit_rank"></td>
                                        <td data-bind="text: hit_at"></td>
                                        <td class="hb-permalink" data-bind="text: permalink"></td>
                                        <td data-bind="text: $parents[0].formatDecimal(human_score_base)"></td>
                                        <td data-bind="text: bot_score == 100 ? 'yes' : 'no'"></td>
                                        <td data-bind="text: human_hit_classification"></td>
                                        <td data-bind="text: bot_hit_classification"></td>
                                        <td data-bind="text: $parents[0].formatHitFlags($data)"></td>
                                        <td data-bind="text: $parents[0].formatHitReasons($data)"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="hb-section hb-empty" data-bind="visible: !hasData() && !isLoading() && !errorMessage()">
                        <h3>No saved scoring rows found</h3>
                        <p>Build the rolling score tables for this brand and date, then refresh this dashboard.</p>
                    </section>
                </div>
            </div>
        `;
    }

    protected buildViewModel(): Object {

        let self = this;

        return {
            account: ko.observable(''),
            brands: ko.observableArray([{value: 'all', label: 'All brands'}]),
            selectedBrand: ko.observable('all'),
            reportDate: ko.observable(''),
            lookbackDays: ko.observable(30),
            rollingHitLimit: ko.observable(10),
            configVersion: ko.observable('v2'),
            limit: ko.observable(200),
            isLoading: ko.observable(false),
            errorMessage: ko.observable(''),
            hasData: ko.observable(false),
            lastRunText: ko.observable('Not loaded yet'),
            scoreCards: ko.observableArray([]),
            comparisonBars: ko.observableArray([]),
            hasTrend: ko.observable(false),
            trendLegend: ko.observableArray([]),
            totalTrafficTrendPoints: ko.observable(''),
            oldExcludedTrafficTrendPoints: ko.observable(''),
            newExcludedTrafficTrendPoints: ko.observable(''),
            botTrafficTrendPoints: ko.observable(''),
            totalTrafficSpikeMarkers: ko.observableArray([]),
            oldExcludedTrafficSpikeMarkers: ko.observableArray([]),
            newExcludedTrafficSpikeMarkers: ko.observableArray([]),
            botTrafficSpikeMarkers: ko.observableArray([]),
            trendStartLabel: ko.observable(''),
            trendEndLabel: ko.observable(''),
            trendMaxLabel: ko.observable(''),
            flatnessSummary: ko.observable(''),
            spikeRuleText: ko.observable('Daily spikes compare each day with the prior loaded days.'),
            spikeSummaryCards: ko.observableArray([]),
            spikeTableRows: ko.observableArray([]),
            hasSpikeAttribution: ko.observable(false),
            spikeAttributionDescription: ko.observable(''),
            spikeAttributionRollups: ko.observableArray([]),
            spikeAttributionTopIps: ko.observableArray([]),
            spikeAttributionFocusDate: ko.observable(''),
            selectedSpikeAttributionBucket: ko.observable('old_clean_and_new_human'),
            spikeAttributionBucketOptions: ko.observableArray([
                {value: '', label: 'Any'},
                {value: 'old_clean_and_new_human', label: 'Old clean and new human'},
                {value: 'old_clean_but_new_suspicious', label: 'Old clean but new suspicious'},
                {value: 'old_clean_but_new_bot', label: 'Old clean but new bot'},
                {value: 'old_suspicious_but_new_human', label: 'Old suspicious but new human'},
                {value: 'old_suspicious_and_new_bot', label: 'Old suspicious and new bot'},
                {value: 'other', label: 'Other'},
            ]),
            coverage: ko.observableArray([]),
            buckets: ko.observableArray([]),
            candidates: ko.observableArray([]),
            ipDetail: ko.observableArray([]),
            selectedIp: ko.observable(''),
            selectedIpBrand: ko.observable(''),
            selectedIpSummary: ko.observable(''),
            baseFormulaSummary: ko.observable(''),
            basePositiveSignals: ko.observableArray([]),
            botFormulaSummary: ko.observable(''),
            botThresholds: ko.observableArray([]),
            botSignals: ko.observableArray([]),
            finalRules: ko.observableArray([]),
            excludedHitFields: ko.observableArray([]),
            finalClassificationFilter: ko.observable(''),
            oldSuspiciousFilter: ko.observable(''),
            bucketFilter: ko.observable(''),
            minHumanScore: ko.observable(''),
            maxHumanScore: ko.observable(''),
            hardBotFilter: ko.observable(''),
            confirmedHumanFilter: ko.observable(''),
            bucketOptions: ko.observableArray([
                {value: '', label: 'Any'},
                {value: 'old_suspicious_and_new_bot', label: 'Old suspicious and new bot'},
                {value: 'old_suspicious_but_new_human', label: 'Old suspicious but new human'},
                {value: 'old_clean_but_new_suspicious', label: 'Old clean but new suspicious'},
                {value: 'old_clean_but_new_bot', label: 'Old clean but new bot'},
                {value: 'old_clean_and_new_human', label: 'Old clean and new human'},
            ]),
            onRefresh: function() {
                self.caller.refreshReport();
            },
            onCandidateClick: function(row) {
                self.caller.showIpDetail(row);
            },
            onClearIpDetail: function() {
                self.caller.clearIpDetail();
            },
            formatNumber: function(value) {
                return self.formatNumber(value);
            },
            formatDecimal: function(value) {
                return self.formatDecimal(value);
            },
            formatPercent: function(value) {
                return self.formatPercent(value);
            },
            formatReasons: function(value) {
                return self.formatReasons(value);
            },
            formatHitFlags: function(row) {
                return self.formatHitFlags(row);
            },
            formatHitReasons: function(row) {
                return self.formatHitReasons(row);
            },
        };
    }

    public getViewModelForRequest(): Object {

        const vm = this.getTemplateViewModel();

        return {
            brands: vm.selectedBrand(),
            date: vm.reportDate(),
            lookback_days: vm.lookbackDays(),
            rolling_hit_limit: vm.rollingHitLimit(),
            config_version: vm.configVersion(),
            limit: vm.limit(),
            final_classification: vm.finalClassificationFilter(),
            old_suspicious: vm.oldSuspiciousFilter(),
            bucket: vm.bucketFilter(),
            min_human_score: vm.minHumanScore(),
            max_human_score: vm.maxHumanScore(),
            has_hard_bot: vm.hardBotFilter(),
            has_confirmed_human: vm.confirmedHumanFilter(),
        };
    }

    public updateReferences(data): void {

        const vm = this.getTemplateViewModel();
        const brands = [{value: 'all', label: 'All brands'}].concat(data.brands || []);

        vm.account(data.account || '');
        vm.brands(brands);
        vm.selectedBrand('all');
        vm.reportDate(data.default_date || '');
        vm.lookbackDays(data.default_lookback_days || 30);
        vm.rollingHitLimit(data.default_rolling_hit_limit || 10);
        vm.configVersion(data.default_config_version || 'v2');
    }

    public updateReport(data): void {

        const vm = this.getTemplateViewModel();
        const headline = (data.headline_comparison && data.headline_comparison[0]) ? data.headline_comparison[0] : {};
        const totalIps = Number(headline.total_ips || 0);

        vm.account((data.run_setup && data.run_setup.account) || vm.account());
        vm.coverage(data.coverage || []);
        vm.buckets(data.buckets || []);
        vm.candidates(data.candidates || []);
        vm.ipDetail(data.ip_detail || []);
        vm.hasData(totalIps > 0);
        vm.lastRunText(this.buildRunText(data.run_setup || {}));
        vm.scoreCards(this.buildScoreCards(headline));
        vm.comparisonBars(this.buildComparisonBars(headline));
        this.updateTrend(data.trend_comparison || [], data.spike_analysis || {});
        this.updateSpikeAttribution(data.spike_attribution || {});
        this.updateFormula(data.formula || {});

        if (vm.selectedIp()) {
            vm.selectedIpSummary(vm.selectedIp() + ' on ' + (vm.selectedIpBrand() || 'selected brand') + ' - latest rolling hits');
        }
    }

    public setLoading(isLoading: boolean): void {

        this.getTemplateViewModel().isLoading(isLoading);
    }

    public setError(message: string): void {

        this.getTemplateViewModel().errorMessage(message);
    }

    public setSelectedIp(ip: string, brand: string): void {

        const vm = this.getTemplateViewModel();
        vm.selectedIp(ip);
        vm.selectedIpBrand(brand);
        vm.selectedIpSummary(ip + ' on ' + (brand || 'selected brand') + ' - loading latest rolling hits');
    }

    public clearIpDetail(): void {

        const vm = this.getTemplateViewModel();
        vm.selectedIp('');
        vm.selectedIpBrand('');
        vm.selectedIpSummary('');
        vm.ipDetail([]);
    }

    protected buildScoreCards(headline): Array<Object> {

        return [
            {label: 'Total IPs', value: this.formatNumber(headline.total_ips), detail: this.formatNumber(headline.total_requests) + ' requests', tone: 'neutral'},
            {label: 'Eligible hits', value: this.formatNumber(headline.total_eligible_hits), detail: 'stored rolling hits', tone: 'neutral'},
            {label: 'Old suspicious', value: this.formatPercent(headline.old_suspicious_pct), detail: 'raw isSuspicious baseline', tone: 'warning'},
            {label: 'New suspicious', value: this.formatPercent(headline.new_suspicious_pct), detail: 'ties are suspicious without confirmation', tone: 'warning'},
            {label: 'New bot', value: this.formatPercent(headline.new_bot_pct), detail: 'hard bot evidence only', tone: 'danger'},
            {label: 'New human', value: this.formatPercent(headline.new_human_pct), detail: 'human majority and no bot hit', tone: 'success'},
            {label: 'Delta', value: this.formatPercent(headline.delta_new_suspicious_or_bot_vs_old_pct), detail: 'new suspicious or bot minus old', tone: 'info'},
        ];
    }

    protected buildComparisonBars(headline): Array<Object> {

        const rows = [
            {label: 'Old suspicious', pct: headline.old_suspicious_pct, tone: 'warning'},
            {label: 'New suspicious', pct: headline.new_suspicious_pct, tone: 'warning'},
            {label: 'New bot', pct: headline.new_bot_pct, tone: 'danger'},
            {label: 'New human', pct: headline.new_human_pct, tone: 'success'},
        ];

        return rows.map((row) => {
            const percent = Math.max(0, Math.min(100, Number(row.pct || 0)));

            return {
                label: row.label,
                value: this.formatPercent(percent),
                width: percent + '%',
                tone: row.tone,
            };
        });
    }

    protected updateFormula(formula): void {

        const vm = this.getTemplateViewModel();
        const hitFormula = formula.hit_level_human_score || {};
        const botFormula = formula.bot_score || {};

        vm.baseFormulaSummary(
            'Each eligible hit starts at ' + (hitFormula.starts_at || 50) +
            '. Human is ' + (hitFormula.human_threshold || 70) +
            ' or higher. Low human is below ' + (hitFormula.low_threshold || 45) +
            '. A valid page view adds only a small baseline; it is not proof of human behavior.'
        );
        vm.basePositiveSignals(
            this.signalRows(hitFormula.base_positive_signals || {}).concat(this.signalRows(hitFormula.base_negative_signals || {}))
        );
        vm.botFormulaSummary(this.botFormulaSummary(botFormula));
        vm.botThresholds(this.botThresholdRows(botFormula));
        vm.botSignals(this.botSignalRows(botFormula.hard_conditions || {}, botFormula.thresholds || {}, botFormula.bad_user_agent_patterns || []));
        vm.finalRules(this.ruleRows(formula.final_ip_classification_rules || {}));
        vm.excludedHitFields(hitFormula.excluded_from_hit_scoring || []);
    }

    protected updateTrend(rows, spikeAnalysis): void {

        const vm = this.getTemplateViewModel();
        const trendRows = Array.isArray(rows) ? rows : [];
        if (trendRows.length === 0) {
            vm.hasTrend(false);
            vm.trendLegend([]);
            vm.totalTrafficTrendPoints('');
            vm.oldExcludedTrafficTrendPoints('');
            vm.newExcludedTrafficTrendPoints('');
            vm.botTrafficTrendPoints('');
            vm.totalTrafficSpikeMarkers([]);
            vm.oldExcludedTrafficSpikeMarkers([]);
            vm.newExcludedTrafficSpikeMarkers([]);
            vm.botTrafficSpikeMarkers([]);
            vm.trendStartLabel('');
            vm.trendEndLabel('');
            vm.trendMaxLabel('');
            vm.flatnessSummary('');
            vm.spikeSummaryCards([]);
            vm.spikeTableRows([]);
            return;
        }

        const reportDate = String(vm.reportDate() || '');
        const selectedIndex = trendRows.map((row) => String(row.report_date || '')).lastIndexOf(reportDate);
        const endIndex = selectedIndex >= 0 ? selectedIndex : trendRows.length - 1;
        const dailyRows = trendRows.slice(0, endIndex + 1);
        const totalValues = dailyRows.map((row) => Number(row.total_requests || 0));
        const oldExcludedValues = dailyRows.map((row) => Math.max(0, Number(row.old_remaining_requests || (Number(row.total_requests || 0) - Number(row.old_suspicious_requests || 0)))));
        const newExcludedValues = dailyRows.map((row) => Math.max(0, Number(row.new_remaining_requests || (Number(row.total_requests || 0) - Number(row.new_suspicious_or_bot_requests || 0)))));
        const botValues = dailyRows.map((row) => Number(row.bot_requests || row.new_bot_only_requests || 0));
        const maxValue = Math.max(1, Math.ceil(Math.max.apply(null, totalValues.concat(oldExcludedValues).concat(newExcludedValues).concat(botValues))));
        const latest = dailyRows[dailyRows.length - 1] || {};
        const lines = Array.isArray(spikeAnalysis.lines) ? spikeAnalysis.lines : [];

        vm.hasTrend(true);
        vm.totalTrafficTrendPoints(this.buildTrendPoints(totalValues, maxValue));
        vm.oldExcludedTrafficTrendPoints(this.buildTrendPoints(oldExcludedValues, maxValue));
        vm.newExcludedTrafficTrendPoints(this.buildTrendPoints(newExcludedValues, maxValue));
        vm.botTrafficTrendPoints(this.buildTrendPoints(botValues, maxValue));
        vm.totalTrafficSpikeMarkers(this.buildTrendSpikeMarkers(dailyRows, totalValues, maxValue, this.spikeLine(lines, 'total_requests')));
        vm.oldExcludedTrafficSpikeMarkers(this.buildTrendSpikeMarkers(dailyRows, oldExcludedValues, maxValue, this.spikeLine(lines, 'old_remaining_requests')));
        vm.newExcludedTrafficSpikeMarkers(this.buildTrendSpikeMarkers(dailyRows, newExcludedValues, maxValue, this.spikeLine(lines, 'new_remaining_requests')));
        vm.botTrafficSpikeMarkers(this.buildTrendSpikeMarkers(dailyRows, botValues, maxValue, this.spikeLine(lines, 'bot_requests')));
        vm.trendStartLabel(String((dailyRows[0] || {}).report_date || reportDate));
        vm.trendEndLabel(String(latest.report_date || reportDate));
        vm.trendMaxLabel('Top ' + this.formatNumber(maxValue) + ' requests');
        vm.spikeRuleText((spikeAnalysis.rule && spikeAnalysis.rule.description) || 'A spike is a day materially above the prior loaded days.');
        vm.flatnessSummary((spikeAnalysis.flatness_comparison && spikeAnalysis.flatness_comparison.summary) || '');
        vm.spikeSummaryCards(this.buildSpikeSummaryCards(lines));
        vm.spikeTableRows(this.buildSpikeTableRows(lines));
        vm.trendLegend([
            {label: 'Total traffic', value: this.formatNumber(Number(latest.total_requests || 0)), tone: 'total'},
            {label: 'Excluding old suspicious', value: this.formatNumber(Math.max(0, Number(latest.old_remaining_requests || (Number(latest.total_requests || 0) - Number(latest.old_suspicious_requests || 0))))), tone: 'old-exclude'},
            {label: 'Excluding new suspicious', value: this.formatNumber(Math.max(0, Number(latest.new_remaining_requests || (Number(latest.total_requests || 0) - Number(latest.new_suspicious_or_bot_requests || 0))))), tone: 'new-exclude'},
            {label: 'Bots', value: this.formatNumber(Number(latest.bot_requests || latest.new_bot_only_requests || 0)), tone: 'bot'},
        ]);
    }

    protected buildTrendPoints(values: Array<number>, maxValue: number): string {

        const width = 588;
        const height = 180;
        const xStart = 32;
        const yStart = 208;
        const count = Math.max(values.length - 1, 1);

        return values.map((value, index) => {
            const x = xStart + (index / count) * width;
            const y = yStart - (Math.max(0, value) / Math.max(maxValue, 1)) * height;
            return x.toFixed(2) + ',' + y.toFixed(2);
        }).join(' ');
    }

    protected buildTrendSpikeMarkers(rows: Array<Object>, values: Array<number>, maxValue: number, line): Array<Object> {

        const spikes = line && Array.isArray(line.spikes) ? line.spikes : [];
        const width = 588;
        const height = 180;
        const xStart = 32;
        const yStart = 208;
        const count = Math.max(values.length - 1, 1);
        const markers = [];

        spikes.forEach((spike) => {
            const date = String(spike.date || '');
            const index = rows.map((row) => String(row['report_date'] || '')).indexOf(date);
            if (index < 0) {
                return;
            }

            markers.push({
                x: (xStart + (index / count) * width).toFixed(2),
                y: (yStart - (Math.max(0, values[index]) / Math.max(maxValue, 1)) * height).toFixed(2),
                title: (line.label || 'Traffic') + ' spike on ' + date + ': ' + this.formatNumber(values[index]),
            });
        });

        return markers;
    }

    protected spikeLine(lines: Array<Object>, key: string): Object {

        for (let index = 0; index < lines.length; index++) {
            if (lines[index]['key'] === key) {
                return lines[index];
            }
        }

        return {};
    }

    protected buildSpikeSummaryCards(lines: Array<Object>): Array<Object> {

        return lines.map((line) => {
            return {
                label: line['label'] || '',
                value: this.formatPercent(line['volatility_pct'] || 0),
                detail: this.formatNumber(line['spike_count'] || 0) + ' spike days - peak ' + this.formatNumber(line['peak_value'] || 0) + ' on ' + String(line['peak_date'] || 'n/a'),
                tone: line['tone'] || '',
            };
        });
    }

    protected buildSpikeTableRows(lines: Array<Object>): Array<Object> {

        const rows = [];
        lines.forEach((line) => {
            const spikes = Array.isArray(line['spikes']) ? line['spikes'] : [];
            spikes.forEach((spike) => {
                rows.push({
                    label: line['label'] || '',
                    date: spike.date || '',
                    value: this.formatNumber(spike.value || 0),
                    increase: '+' + this.formatNumber(spike.increase || 0) + ' (' + this.formatPercent(spike.increase_pct || 0) + ')',
                });
            });
        });

        return rows;
    }

    protected updateSpikeAttribution(attribution): void {

        const vm = this.getTemplateViewModel();
        const rollups = Array.isArray(attribution.rollups) ? attribution.rollups : [];
        const topIps = Array.isArray(attribution.top_ips) ? attribution.top_ips : [];
        const spikeDates = Array.isArray(attribution.spike_dates) ? attribution.spike_dates : [];
        const focusDate = spikeDates.length > 0 ? String(spikeDates[spikeDates.length - 1] || '') : '';

        vm.hasSpikeAttribution(rollups.length > 0 || topIps.length > 0);
        vm.spikeAttributionDescription(String(attribution.description || 'Top contributors for spike days still visible after applying the new suspicious/bot exclusion.'));
        vm.spikeAttributionFocusDate(focusDate || 'No remaining spike date');
        vm.spikeAttributionRollups(this.buildSpikeAttributionRollups(rollups));
        vm.spikeAttributionTopIps(this.buildSpikeAttributionTopIps(topIps, focusDate));
    }

    protected buildSpikeAttributionRollups(rows: Array<Object>): Array<Object> {

        return rows.map((row) => {
            return {
                report_date: String(row['report_date'] || ''),
                bucket: String(row['bucket'] || ''),
                bucket_label: this.labelize(String(row['bucket'] || 'other')),
                request_count_text: this.formatNumber(row['request_count'] || 0) + ' requests',
                request_pct_text: this.formatPercent(row['request_pct'] || 0),
                detail: this.formatNumber(row['ip_count'] || 0) + ' IPs - ' + this.formatNumber(row['confirmed_human_ip_count'] || 0) + ' confirmed human - ' + this.formatNumber(row['hard_bot_ip_count'] || 0) + ' hard bot',
                tone: this.attributionTone(String(row['bucket'] || '')),
            };
        });
    }

    protected buildSpikeAttributionTopIps(rows: Array<Object>, focusDate: string): Array<Object> {

        const filteredRows = focusDate ? rows.filter((row) => String(row['report_date'] || '') === focusDate) : rows;

        return filteredRows.map((row) => {
            return {
                report_date: String(row['report_date'] || ''),
                ip: row['ip'] || '',
                brand: row['brand'] || '',
                bucket: String(row['bucket'] || ''),
                bucket_label: this.labelize(String(row['bucket'] || 'other')),
                old_is_suspicious: Number(row['old_is_suspicious'] || 0),
                final_ip_classification: row['final_ip_classification'] || '',
                request_count_text: this.formatNumber(row['request_count'] || 0),
                rolling_hit_count: this.formatNumber(row['rolling_hit_count'] || 0),
                human_hits: this.formatNumber(row['human_hits'] || 0),
                suspicious_hits: this.formatNumber(row['suspicious_hits'] || 0),
                bot_hits: this.formatNumber(row['bot_hits'] || 0),
                avg_human_score: this.formatDecimal(row['avg_human_score'] || 0),
                has_hard_bot: Number(row['has_hard_bot'] || 0),
                has_confirmed_human: Number(row['has_confirmed_human'] || 0),
                final_reasons_text: this.formatReasons(row['final_reasons']),
            };
        });
    }

    protected attributionTone(bucket: string): string {

        if (bucket.indexOf('new_human') >= 0) {
            return 'success';
        }

        if (bucket.indexOf('new_bot') >= 0) {
            return 'danger';
        }

        if (bucket.indexOf('new_suspicious') >= 0) {
            return 'warning';
        }

        return 'neutral';
    }

    protected signalRows(signals): Array<Object> {

        return Object.keys(signals).filter((key) => {
            const signal = signals[key] || {};
            return typeof signal.weight === 'undefined' || Number(signal.weight || 0) !== 0;
        }).map((key) => {
            const signal = signals[key] || {};
            const parts = [];
            if (typeof signal.weight !== 'undefined') {
                parts.push('weight ' + signal.weight);
            }
            if (typeof signal.threshold !== 'undefined') {
                parts.push('threshold ' + signal.threshold);
            }
            if (signal.hard) {
                parts.push('hard bot signal');
            }
            if (signal.description) {
                parts.push(signal.description);
            }

            return {
                label: this.labelize(key) + ': ',
                detail: parts.join(' - '),
            };
        });
    }

    protected botFormulaSummary(botFormula): string {

        if (botFormula.mode === 'hard_conditions_only') {
            return String(botFormula.description || 'Bot detection uses hard OR conditions only. Trap page, robot classification, or known bad user-agent classifies the hit as bot.');
        }

        return String(botFormula.description || '');
    }

    protected botThresholdRows(botFormula): Array<Object> {

        if (botFormula.mode === 'hard_conditions_only') {
            return [
                {
                    label: 'Bot rule: ',
                    detail: 'bot is true only when trap page OR raw robot classification OR known bad crawler/headless/tool user-agent is true.',
                },
                {
                    label: 'Compatibility score: ',
                    detail: 'stored bot_score is 100 for hard bot and 0 otherwise; it is not an accumulated score.',
                },
            ];
        }

        const thresholds = botFormula.thresholds || {};
        const patterns = botFormula.bad_user_agent_patterns || [];
        const patternText = Array.isArray(patterns) && patterns.length > 0 ? patterns.slice(0, 8).join(', ') : 'configured crawler/headless/tool patterns';

        return [
            {
                label: 'High velocity: ',
                detail: 'active when request rate is at least ' + this.valueOrFallback(thresholds.requests_per_minute, 'the configured limit') + ' per minute, or when page views reach ' + this.valueOrFallback(thresholds.page_views_per_window, 'the configured limit') + ' inside ' + this.valueOrFallback(thresholds.velocity_window_minutes, 'the configured') + ' minutes.',
            },
            {
                label: 'User-agent and brand spread: ',
                detail: 'many user agents means at least ' + this.valueOrFallback(thresholds.max_user_agents, 'the configured number of') + ' unique user agents for the IP. Many brands means at least ' + this.valueOrFallback(thresholds.max_brands, 'the configured number of') + ' selected brands touched by the IP.',
            },
            {
                label: 'Engagement/referrer checks: ',
                detail: 'no-engagement means the hit has no useful engagement, no ishc, and no login/session. No-referrer/no-engagement also requires at least ' + this.valueOrFallback(thresholds.no_referrer_no_engagement_hits, 'the configured number of') + ' no-referrer page hits for the IP.',
            },
            {
                label: 'Bad user-agent checks: ',
                detail: 'malformed user-agent means missing or shorter than ' + this.valueOrFallback(thresholds.malformed_user_agent_min_length, 'the configured minimum') + ' characters. Known bad patterns include: ' + patternText + '.',
            },
        ];
    }

    protected botSignalRows(signals, thresholds, badUserAgentPatterns): Array<Object> {

        return Object.keys(signals).filter((key) => {
            const signal = signals[key] || {};
            return typeof signal.weight === 'undefined' || Number(signal.weight || 0) !== 0;
        }).map((key) => {
            const signal = signals[key] || {};
            const parts = [this.botSignalExplanation(key, thresholds, badUserAgentPatterns)];
            if (signal.hard) {
                parts.push('hard bot flag: yes');
            } else {
                parts.push('hard bot flag: no');
            }

            return {
                label: this.labelize(key) + ': ',
                detail: parts.join(' - '),
            };
        });
    }

    protected botSignalExplanation(key: string, thresholds, badUserAgentPatterns): string {

        const patterns = Array.isArray(badUserAgentPatterns) ? badUserAgentPatterns.slice(0, 6).join(', ') : '';
        const explanations = {
            trap_page_hit: 'the hit lands on a configured trap page.',
            robot_classification: 'the raw hit has robotName or robotCategory metadata.',
            known_bad_crawler_user_agent: 'the user-agent contains a configured bad pattern' + (patterns ? ' such as ' + patterns : '') + '.',
            malformed_user_agent: 'the user-agent is empty or shorter than ' + this.valueOrFallback(thresholds.malformed_user_agent_min_length, 'the configured minimum') + ' characters.',
            high_velocity: 'the IP reaches at least ' + this.valueOrFallback(thresholds.requests_per_minute, 'the configured request limit') + ' requests per minute or ' + this.valueOrFallback(thresholds.page_views_per_window, 'the configured page-view limit') + ' page views in ' + this.valueOrFallback(thresholds.velocity_window_minutes, 'the configured') + ' minutes.',
            many_user_agents: 'the IP uses at least ' + this.valueOrFallback(thresholds.max_user_agents, 'the configured number of') + ' unique user agents.',
            many_brands: 'the IP touches at least ' + this.valueOrFallback(thresholds.max_brands, 'the configured number of') + ' selected brands.',
            repeated_no_engagement: 'this hit has no meaningful engagement, no ishc, and no login/session signal.',
            identical_timing_gaps: 'the IP has a same-second burst above the normal request-pattern limit.',
            no_referrer_no_engagement: 'the IP has at least ' + this.valueOrFallback(thresholds.no_referrer_no_engagement_hits, 'the configured number of') + ' no-referrer page hits and this hit has zero engaged seconds.',
            excessive_uuid_rotation: 'the IP has at least ' + this.valueOrFallback(thresholds.max_uuids, 'the configured number of') + ' UUIDs. UUID never adds human score.',
            suspicious_referrer_permalink: 'the permalink/referrer is invalid, uses javascript:, or contains path traversal using two dots.',
        };

        return explanations[key] || 'that configured bot flag is true.';
    }

    protected valueOrFallback(value, fallback: string): string {

        if (typeof value === 'undefined' || value === null || value === '') {
            return fallback;
        }

        return String(value);
    }

    protected ruleRows(rules): Array<Object> {

        return Object.keys(rules).map((key) => {
            const rule = rules[key];
            if (rule && typeof rule === 'object' && typeof rule.value !== 'undefined') {
                return {
                    label: this.labelize(key) + ': ',
                    detail: 'value ' + String(rule.value) + ' - ' + String(rule.description || ''),
                };
            }

            return {
                label: this.labelize(key) + ': ',
                detail: String(rule),
            };
        });
    }

    protected buildRunText(runSetup): string {

        const date = runSetup.report_date || this.getTemplateViewModel().reportDate();
        const brands = (runSetup.selected_brands || []).join(', ') || this.getTemplateViewModel().selectedBrand();

        return brands + ' - ' + date + ' - new vs old';
    }

    protected labelize(value: string): string {

        return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    protected formatNumber(value): string {

        const number = Number(value || 0);
        return number.toLocaleString('en-EG', {minimumFractionDigits: 0, maximumFractionDigits: 0});
    }

    protected formatDecimal(value): string {

        const number = Number(value || 0);
        return number.toLocaleString('en-EG', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }

    protected formatPercent(value): string {

        return this.formatDecimal(value) + '%';
    }

    protected formatReasons(value): string {

        if (Array.isArray(value)) {
            return value.join('; ');
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed.join('; ') : value;
            } catch (error) {
                return value;
            }
        }

        return '';
    }

    protected formatHitFlags(row): string {

        const flags = [];
        if (Number(row.has_ishc || 0) > 0) {
            flags.push('raw ishc');
        }
        if (Number(row.trap_page_flag || 0) > 0) {
            flags.push('trap');
        }
        if (Number(row.robot_flag || 0) > 0) {
            flags.push('robot');
        }
        if (Number(row.hard_bot_flag || 0) > 0) {
            flags.push('hard bot');
        }

        return flags.length ? flags.join(', ') : 'none';
    }

    protected formatHitReasons(row): string {

        const groups = [
            this.formatReasons(row.human_positive_reasons),
            this.formatReasons(row.human_negative_reasons),
            this.formatReasons(row.bot_reasons),
        ].filter((value) => value !== '');

        return groups.join(' | ');
    }
}



// WEBPACK FOOTER //
// ./js/components/templates/humanBotScoreDashboard_template.ts